import React from 'react';

import { GoogleButton } from '@/app/[locale]/(auth)/components/GoogleButton';
import { decodeJWT } from '@/modules/auth/decodeJWT';
import { SSOButtonVariant } from '@/modules/auth/types';
import { useRedirectQueryParam } from '@/modules/auth/useRedirectQueryParam';
import {
  handleSSOError,
  setAccountLinkingCookies,
} from '@/modules/login/helpers';
import { LoginType } from '@/modules/login/types';
import { useLoginMutations } from '@/modules/login/useLoginMutations';
import { useExtendedRouter } from '@/modules/routing/useExtendedRouter';
import { useSSOSignup } from '@/modules/signup/hooks/useSSOSignup';
import { useSignupTracking } from '@/modules/signup/hooks/useSignupTracking';
import { SignupType } from '@/modules/signup/types';

type Props = {
  nonce: string;
  ssoButtonVariant: SSOButtonVariant;
  setError: React.Dispatch<React.SetStateAction<string>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

export function GoogleSignupButton({
  nonce,
  ssoButtonVariant,
  setError,
  setIsLoading,
}: Props) {
  const { signupWithSSO } = useSSOSignup({
    ssoType: SignupType.Google,
  });
  const { loginMutation } = useLoginMutations({
    loginType: LoginType.GoogleSignup,
  });
  const { sendSignupSubmitActionEvent } = useSignupTracking();
  const router = useExtendedRouter();
  const redirectQueryParam = useRedirectQueryParam();

  async function handleGoogle({
    credential,
  }: google.accounts.id.CredentialResponse) {
    const decodedUserInfo = decodeJWT(credential);
    const { email, given_name, family_name } = decodedUserInfo;

    function handleUserNotFound() {
      signupWithSSO({
        name: {
          firstName: given_name,
          lastName: family_name,
        },
        email,
        credentials: { googleIdToken: credential },
      });
    }

    function handleAccountNotLinked() {
      setAccountLinkingCookies({ googleIdToken: credential }, email);

      router.push(`/login/${redirectQueryParam}`);
    }

    setIsLoading(true);

    loginMutation.mutate(
      { google_id_token: credential },
      {
        onError: (error) => {
          handleSSOError({
            error,
            setError,
            onUserNotFound: handleUserNotFound,
            onAccountNotLinked: handleAccountNotLinked,
          });
        },
      }
    );
  }

  return (
    <GoogleButton
      handleResponse={handleGoogle}
      nonce={nonce}
      variant={
        ssoButtonVariant === SSOButtonVariant.Icon
          ? SSOButtonVariant.Icon
          : SSOButtonVariant.EyeCatching
      }
      onSubmitTracking={() => sendSignupSubmitActionEvent(SignupType.Google)}
    />
  );
}
